import React, { useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";

import { Card, CardContent } from "@components/ui/card";
import ModelRunsCard from "@components/ui/ModelRunsCard";
import { ECON_API_BASE_URL } from "@lib/config";
import { fmtLocalDate, fmtLocalDateTime } from "@lib/dateUtils";
import { useTheme } from "../../theme/ThemeProvider";
import { getMlChartPalette } from "../../theme/chartTheme";

type LatestFileMeta = {
  blob: string;
  size: number;
  last_modified: string;
  etag: string;
  url: string;
};

type LatestResponse = {
  container: string;
  prefix: string;
  files: {
    macro_features?: LatestFileMeta;
    macro_labels?: LatestFileMeta;
    regime_probs?: LatestFileMeta;
    model?: LatestFileMeta;
    shap_png?: LatestFileMeta;
    early_warning_json?: LatestFileMeta;
    early_warning_csv?: LatestFileMeta;
    manifest?: LatestFileMeta;
    walk_forward_production_recommendation?: LatestFileMeta;
    walk_forward_backtest_metrics?: LatestFileMeta;
    walk_forward_threshold_sweep?: LatestFileMeta;
    walk_forward_calibration_report?: LatestFileMeta;
    walk_forward_false_signal_ledger?: LatestFileMeta;
    walk_forward_fold_metrics?: LatestFileMeta;
  };
  account_url: string;
};

type EarlyWarningPayload = {
  generated_at: string;
  generated_at_utc?: string;
  as_of?: string;
  signal?: string;
  threshold?: number;
  thresholds?: {
    sentinel?: number;
    watch?: number;
    caution?: number;
    severe?: number;
    recovery?: number;
  };
  operating_bands?: Record<string, { lower?: number; upper?: number; use?: string }>;
  radar_state?: string;
  rolling_window_days?: number;
  rolling_weakening_prob?: number;
  rolling_severity_score?: number;
  weakening_prob?: number;
  severity_score?: number;
  delta_3m?: number;
  latest_trigger?: { kind?: string; date: string; weakening_prob: number; delta_3m: number } | null;
  latest_recovery?: { kind?: string; date: string; weakening_prob: number; delta_3m: number } | null;
  triggers: Array<{ kind: string; date: string; weakening_prob: number; delta_3m: number }>;
  recoveries: Array<{ kind: string; date: string; weakening_prob: number; delta_3m: number }>;
};

type RegimePoint = {
  date: string;
  expansionary: number;
  cautionary: number;
  recessionary: number;
  weakening: number;
};

type ThresholdMetrics = {
  threshold?: number;
  weakening_precision?: number;
  weakening_recall?: number;
  weakening_f1?: number;
  false_positive_rate?: number;
  false_negative_rate?: number;
  event_detection_rate?: number;
  all_events_detection_rate?: number;
  macro_deterioration_events_detection_rate?: number;
  exogenous_shock_events_detection_rate?: number;
  avg_lead_months_detected?: number;
  avg_macro_lead_months_detected?: number;
  signal_churn_rate?: number;
  tn?: number;
  fp?: number;
  fn?: number;
  tp?: number;
};

type OperatingBand = {
  lower?: number;
  upper?: number;
  use?: string;
};

type WalkForwardProductionRecommendation = {
  generated_at_utc?: string;
  status?: string;
  summary?: string;
  model_use?: string;
  do_not_use_as?: string;
  execution_boundary?: string;
  operating_bands?: Record<string, OperatingBand>;
  headline_threshold_rows?: Record<string, ThresholdMetrics>;
  event_methodology?: Record<string, string>;
  quality_gate?: {
    enabled?: boolean;
    passed?: boolean;
    failed_checks?: string[];
    checks?: Record<string, boolean>;
    observed?: Record<string, any>;
    thresholds?: Record<string, number>;
    note?: string;
  };
  recommended_next_action?: string;
};

type WalkForwardBacktestMetrics = {
  generated_at_utc?: string;
  methodology?: {
    name?: string;
    description?: string;
    sample_role?: string;
    production_model_note?: string;
  };
  operating_thresholds?: Record<string, number>;
  feature_count?: number;
  rows?: number;
  date_range?: [string, string] | string[];
  requested_folds?: number;
  successful_folds?: number;
};

type WalkForwardCalibrationReport = {
  generated_at_utc?: string;
  methodology?: string;
  rows?: number;
  brier_score?: number;
  expected_calibration_error?: number;
  bins?: Array<{
    bin?: number;
    lower?: number;
    upper?: number;
    count?: number;
    mean_predicted_probability?: number;
    actual_weakening_rate?: number;
  }>;
};

const ui = {
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  sectionBg: "rgba(148, 163, 184, 0.10)",
  shadow: "var(--shadow-soft)",
};

// Chart palette (type + getMlChartPalette) now lives in ../../theme/chartTheme —
// shared macro-regime base, de-duplicated with the Economics dashboard.

const cardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

const softCardStyle: React.CSSProperties = {
  backgroundColor: ui.sectionBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
};

type DashboardRiskStatus = "benign" | "sentinel" | "monitor" | "watch" | "caution" | "high_risk";

type DashboardRiskPresentation = {
  status: DashboardRiskStatus;
  label: string;
  tone: "green" | "amber" | "red";
  summary: string;
  detail: string;
};

function toFraction(n?: number | null): number | null {
  if (n == null || Number.isNaN(n)) return null;
  const value = Number(n);
  if (!Number.isFinite(value)) return null;
  return Math.abs(value) > 1 ? value / 100 : value;
}

function buildDashboardRiskPresentation(ew?: EarlyWarningPayload | null): DashboardRiskPresentation {
  const sentinel = ew?.thresholds?.sentinel ?? 0.25;
  const watch = ew?.thresholds?.watch ?? 0.45;
  const caution = ew?.thresholds?.caution ?? ew?.threshold ?? 0.55;
  const severe = ew?.thresholds?.severe ?? 0.68;
  const rolling = toFraction(ew?.rolling_weakening_prob);
  const weakening = toFraction(ew?.weakening_prob);
  const recovery = ew?.thresholds?.recovery ?? 0.35;
  const effective = rolling ?? weakening ?? 0;
  const signal = (ew?.signal || "").toLowerCase();
  const radarState = (ew?.radar_state || "").toLowerCase();
  const delta = ew?.delta_3m ?? ew?.latest_trigger?.delta_3m ?? 0;

  if (signal === "severe_deterioration" || radarState === "severe_deterioration" || effective >= severe) {
    return {
      status: "high_risk",
      label: "Severe",
      tone: "red",
      summary: "The warning engine is in a severe deterioration band.",
      detail: delta > 0.03
        ? "Weakening probability is above the high-risk band and still accelerating."
        : "Weakening probability is above the high-risk band, though the recent acceleration is more measured.",
    };
  }
  if (signal === "caution" || radarState === "caution" || effective >= caution) {
    return {
      status: "caution",
      label: "Caution",
      tone: "amber",
      summary: "The warning engine is flagging meaningful deterioration risk.",
      detail: "This is intended as a portfolio-awareness state, not a literal recession call.",
    };
  }
  if (signal === "watch" || radarState === "watch" || effective >= watch) {
    return {
      status: "watch",
      label: "Watch",
      tone: "amber",
      summary: "The warning engine is seeing early drift in the macro tape.",
      detail: "Conditions merit CIO monitoring, but this is not a high-conviction warning state.",
    };
  }
  if (radarState === "monitor" || (effective >= recovery && effective < watch)) {
    return {
      status: "monitor",
      label: "Monitor",
      tone: "amber",
      summary: "The engine is above the recovery boundary but below formal Watch.",
      detail: "Monitor is a trend-awareness band. It should be reviewed with drivers and market confirmation, not used as an execution signal.",
    };
  }
  if (radarState === "sentinel" || (effective >= sentinel && effective < recovery)) {
    return {
      status: "sentinel",
      label: "Sentinel",
      tone: "amber",
      summary: "The engine is in early radar territory.",
      detail: "Sentinel is a diagnostics/monitoring band. It is not a formal Watch, Caution, or execution signal.",
    };
  }
  return {
    status: "benign",
    label: "Benign",
    tone: "green",
    summary: "The warning engine is not currently flagging a live macro warning state.",
    detail: "The dashboard read is broadly consistent with an expansionary backdrop.",
  };
}

function pct(n?: number | null, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function deltaPct(n?: number | null, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  const sign = Number(n) > 0 ? "+" : "";
  return `${sign}${(Number(n) * 100).toFixed(digits)} pts`;
}

function num(n?: number | null, digits = 2): string {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toFixed(digits);
}

function formatStatusLabel(value?: string | null): string {
  if (!value) return "Unavailable";
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function boolLabel(value?: boolean | null): string {
  if (value == null) return "—";
  return value ? "Pass" : "Fail";
}

function ratio(n?: number | null, digits = 1): string {
  return pct(n, digits);
}

async function fetchJsonArtifact<T>(filename: string): Promise<T | null> {
  const res = await fetch(proxyUrl(filename), { cache: "no-store" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`${filename} ${res.status}`);
  return (await res.json()) as T;
}

function certificationTone(status?: string | null, gatePassed?: boolean | null): { color: string; bg: string; border: string } {
  const normalized = (status || "").toLowerCase();
  if (gatePassed === false || normalized.includes("fail")) {
    return { color: "var(--status-danger-text)", bg: "var(--status-danger-bg)", border: "var(--status-danger-border)" };
  }
  if (normalized.includes("monitor")) {
    return { color: "var(--status-warning-text)", bg: "var(--status-warning-bg)", border: "var(--status-warning-border)" };
  }
  if (normalized.includes("pass")) {
    return { color: "var(--status-positive-text)", bg: "var(--status-positive-bg)", border: "var(--status-positive-border)" };
  }
  return { color: ui.textPrimary, bg: ui.sectionBg, border: ui.cardBorder };
}

function bandLabel(key: string): string {
  switch (key) {
    case "sentinel_radar": return "Sentinel";
    case "monitor": return "Monitor";
    case "watch": return "Watch";
    case "caution": return "Caution";
    case "severe": return "Severe";
    default: return formatStatusLabel(key);
  }
}

function rangeLabel(band?: OperatingBand): string {
  if (!band) return "—";
  const lower = band.lower != null ? pct(band.lower, 0) : "—";
  const upper = band.upper != null ? pct(band.upper, 0) : "+";
  return `${lower}–${upper}`;
}

function thresholdRowRows(rec?: WalkForwardProductionRecommendation | null): Array<{ key: string; label: string; row: ThresholdMetrics }> {
  const rows = rec?.headline_threshold_rows || {};
  return [
    { key: "sentinel", label: "Sentinel", row: rows.sentinel || {} },
    { key: "watch", label: "Watch", row: rows.watch || {} },
    { key: "caution", label: "Caution", row: rows.caution || {} },
    { key: "severe", label: "Severe", row: rows.severe || {} },
  ];
}

function proxyUrl(filename: string) {
  return `${ECON_API_BASE_URL}/ml/${encodeURIComponent(filename)}`;
}

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }

  if (cell.length || row.length) {
    row.push(cell.replace(/\r$/, ""));
    rows.push(row);
  }

  return rows.filter((r) => r.some((c) => c !== ""));
}

function normalize(text: string): string {
  return (text || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function findColumn(headers: string[], candidates: string[]): number {
  const normalized = headers.map((header) => normalize(header));
  // Prefer an EXACT header match (across all candidates) before falling back to substring,
  // so a later exact column wins over an earlier partial one. Without this, the candidate
  // "expansionary" substring-matched the earlier un-normalized "expansionary_prob" column
  // instead of the model's normalized "expansionary", overstating the Expansionary tile so
  // the three regime tiles didn't sum to 100% (2026-07-01 audit P1 #22). The substring pass
  // is retained because "weakening" only ever matches partially (the headers are
  // weakening_prob_raw / weakening_prob / weakening_prob_pctile_36m).
  for (const candidate of candidates) {
    const idx = normalized.findIndex((value) => value === candidate);
    if (idx >= 0) return idx;
  }
  for (const candidate of candidates) {
    const idx = normalized.findIndex((value) => value.includes(candidate));
    if (idx >= 0) return idx;
  }
  return -1;
}

function parseRegimeRows(table: string[][]): RegimePoint[] {
  if (table.length < 2) return [];

  const headers = table[0];
  const dateIdx = findColumn(headers, ["date", "asof", "timestamp"]);
  const expIdx = findColumn(headers, ["expansionary", "expansion", "growth"]);
  const cautIdx = findColumn(headers, ["cautionary", "neutral", "midcycle", "transit"]);
  const recIdx = findColumn(headers, ["recessionary", "recession", "recprob", "probrec"]);
  const weakeningIdx = findColumn(headers, ["weakening", "weak", "riskweak"]);

  return table
    .slice(1)
    .map((row) => {
      const expansionary = expIdx >= 0 ? Number(row[expIdx]) : 0;
      const cautionary = cautIdx >= 0 ? Number(row[cautIdx]) : 0;
      const recessionary = recIdx >= 0 ? Number(row[recIdx]) : 0;
      const weakening = weakeningIdx >= 0 ? Number(row[weakeningIdx]) : recessionary + 0.5 * cautionary;
      return {
        date: dateIdx >= 0 ? row[dateIdx] : "",
        expansionary: Number.isFinite(expansionary) ? expansionary : 0,
        cautionary: Number.isFinite(cautionary) ? cautionary : 0,
        recessionary: Number.isFinite(recessionary) ? recessionary : 0,
        weakening: Number.isFinite(weakening) ? weakening : 0,
      };
    })
    .filter((row) => row.date)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

const MLResultsTab: React.FC = () => {
  const { theme } = useTheme();
  const chart = useMemo(() => getMlChartPalette(theme), [theme]);
  const [latest, setLatest] = useState<LatestResponse | null>(null);
  const [probsCSV, setProbsCSV] = useState<string[][]>([]);
  const [ew, setEW] = useState<EarlyWarningPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [running, setRunning] = useState<null | "ew" | "ml">(null);
  const [wfRecommendation, setWfRecommendation] = useState<WalkForwardProductionRecommendation | null>(null);
  const [wfMetrics, setWfMetrics] = useState<WalkForwardBacktestMetrics | null>(null);
  const [wfCalibration, setWfCalibration] = useState<WalkForwardCalibrationReport | null>(null);

  const FUNC_KEY = (process.env.REACT_APP_FUNC_KEY || "").trim();
  const keyParam = FUNC_KEY ? `?code=${encodeURIComponent(FUNC_KEY)}` : "";

  async function refreshArtifacts() {
    const [csvRes, ewRes, rec, metrics, calibration] = await Promise.all([
      fetch(proxyUrl("regime_probs.csv"), { cache: "no-store" }),
      fetch(proxyUrl("early_warning.json"), { cache: "no-store" }),
      fetchJsonArtifact<WalkForwardProductionRecommendation>("walk_forward_production_recommendation.json").catch(() => null),
      fetchJsonArtifact<WalkForwardBacktestMetrics>("walk_forward_backtest_metrics.json").catch(() => null),
      fetchJsonArtifact<WalkForwardCalibrationReport>("walk_forward_calibration_report.json").catch(() => null),
    ]);

    if (csvRes.ok) {
      setProbsCSV(parseCSV(await csvRes.text()));
    }
    if (ewRes.ok) {
      setEW(await ewRes.json());
    }
    setWfRecommendation(rec);
    setWfMetrics(metrics);
    setWfCalibration(calibration);
  }

  async function runEarlyWarningNow() {
    try {
      setRunning("ew");
      const res = await fetch(`${ECON_API_BASE_URL}/econ/run_early_warning${keyParam}`, {
        method: "POST",
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`run_early_warning ${res.status}`);
      await refreshArtifacts();
    } catch (e: any) {
      setErr(e?.message || "Failed to run early warning");
    } finally {
      setRunning(null);
    }
  }

  async function runFullMLNow() {
    try {
      setRunning("ml");
      const separator = keyParam ? "&" : "?";
      const res = await fetch(`${ECON_API_BASE_URL}/econ/run_ml_now${keyParam}${separator}train=false`, {
        method: "POST",
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`run_ml_now ${res.status}`);
      await refreshArtifacts();
    } catch (e: any) {
      setErr(e?.message || "Failed to run ML pipeline");
    } finally {
      setRunning(null);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const mres = await fetch(`${ECON_API_BASE_URL}/econ/latest`, { cache: "no-store" });
        if (!mres.ok) throw new Error(`latest manifest ${mres.status}`);
        const manifest: LatestResponse = await mres.json();
        setLatest(manifest);

        const csvRes = await fetch(proxyUrl("regime_probs.csv"), { cache: "no-store" });
        if (csvRes.ok) {
          setProbsCSV(parseCSV(await csvRes.text()));
        } else if (csvRes.status === 404) {
          setProbsCSV([]);
        } else {
          throw new Error(`regime_probs ${csvRes.status}`);
        }

        const ewRes = await fetch(proxyUrl("early_warning.json"), { cache: "no-store" });
        if (ewRes.ok) {
          setEW(await ewRes.json());
        } else if (ewRes.status === 404) {
          setEW(null);
        } else {
          throw new Error(`early_warning ${ewRes.status}`);
        }

        const [rec, metrics, calibration] = await Promise.all([
          fetchJsonArtifact<WalkForwardProductionRecommendation>("walk_forward_production_recommendation.json").catch(() => null),
          fetchJsonArtifact<WalkForwardBacktestMetrics>("walk_forward_backtest_metrics.json").catch(() => null),
          fetchJsonArtifact<WalkForwardCalibrationReport>("walk_forward_calibration_report.json").catch(() => null),
        ]);
        setWfRecommendation(rec);
        setWfMetrics(metrics);
        setWfCalibration(calibration);
      } catch (e: any) {
        setErr(e?.message || "Failed to load ML artifacts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const regimeRows = useMemo(() => parseRegimeRows(probsCSV), [probsCSV]);
  const latestRegime = useMemo(() => (regimeRows.length ? regimeRows[regimeRows.length - 1] : null), [regimeRows]);
  const priorRegime = useMemo(() => (regimeRows.length > 1 ? regimeRows[regimeRows.length - 2] : null), [regimeRows]);
  const recentRows = useMemo(() => regimeRows.slice(-24), [regimeRows]);
  const recentTableRows = useMemo(() => {
    if (probsCSV.length < 2) return [];
    return [...probsCSV.slice(1)].sort((a, b) => new Date((b[0] || "")).getTime() - new Date((a[0] || "")).getTime()).slice(0, 24);
  }, [probsCSV]);
  const headers = useMemo(() => (probsCSV.length ? probsCSV[0] : []), [probsCSV]);
  const dashboardRisk = useMemo(() => buildDashboardRiskPresentation(ew), [ew]);
  const toneColor = dashboardRisk.tone === "red" ? "var(--status-danger-text)" : dashboardRisk.tone === "amber" ? "var(--status-warning-text)" : "var(--status-positive-text)";
  const toneBg = dashboardRisk.tone === "red" ? "var(--status-danger-bg)" : dashboardRisk.tone === "amber" ? "var(--status-warning-bg)" : "var(--status-positive-bg)";
  const toneBorder = dashboardRisk.tone === "red" ? "var(--status-danger-border)" : dashboardRisk.tone === "amber" ? "var(--status-warning-border)" : "var(--status-positive-border)";
  const certTone = certificationTone(wfRecommendation?.status, wfRecommendation?.quality_gate?.passed);
  const thresholdRows = useMemo(() => thresholdRowRows(wfRecommendation), [wfRecommendation]);
  const bandRows = useMemo(() => {
    const bands = wfRecommendation?.operating_bands || ew?.operating_bands || {};
    const order = ["sentinel_radar", "monitor", "watch", "caution", "severe"];
    return order.map((key) => ({ key, band: bands[key] })).filter((item) => item.band);
  }, [wfRecommendation, ew]);

  if (loading) return <p style={{ color: ui.textMuted }}>Loading ML results…</p>;
  if (err) return <p className="text-down">Error: {err}</p>;
  if (!latest) return <p style={{ color: ui.textMuted }}>No ML artifacts found.</p>;

  const dl = {
    macro_features: proxyUrl("macro_features.csv"),
    macro_labels: proxyUrl("macro_labels.csv"),
    model: proxyUrl("macro_model.pkl"),
    early_warning_csv: proxyUrl("early_warning.csv"),
    shap_png: proxyUrl("shap_bar.png"),
    walk_forward_production_recommendation: proxyUrl("walk_forward_production_recommendation.json"),
    walk_forward_backtest_metrics: proxyUrl("walk_forward_backtest_metrics.json"),
    walk_forward_threshold_sweep: proxyUrl("walk_forward_threshold_sweep.json"),
    walk_forward_calibration_report: proxyUrl("walk_forward_calibration_report.json"),
    walk_forward_false_signal_ledger: proxyUrl("walk_forward_false_signal_ledger.csv"),
    walk_forward_fold_metrics: proxyUrl("walk_forward_fold_metrics.csv"),
  };

  return (
    <div className="grid gap-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Forecasting Model Outputs</h2>
          <p className="text-sm" style={{ color: ui.textSecondary }}>
            Source prefix: {latest.prefix}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            className="px-3 py-1.5 rounded text-white disabled:opacity-50"
            style={{ backgroundColor: "#2563eb" }}
            onClick={runEarlyWarningNow}
            disabled={!!running}
          >
            {running === "ew" ? "Recomputing…" : "Recompute Early Warning"}
          </button>
          <button
            className="px-3 py-1.5 rounded text-white disabled:opacity-50"
            style={{ backgroundColor: "#334155" }}
            onClick={runFullMLNow}
            disabled={!!running}
          >
            {running === "ml" ? "Running…" : "Run ML Now"}
          </button>
        </div>
      </div>

      <ModelRunsCard />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card style={cardStyle}>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Expansionary Probability</div>
            <div className="text-2xl font-semibold" style={{ color: chart.expansionary }}>{pct(latestRegime?.expansionary)}</div>
            <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>
              Δ vs prior: {deltaPct((latestRegime?.expansionary ?? 0) - (priorRegime?.expansionary ?? 0))}
            </div>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Caution Probability</div>
            <div className="text-2xl font-semibold" style={{ color: chart.cautionary }}>{pct(latestRegime?.cautionary)}</div>
            <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>
              Δ vs prior: {deltaPct((latestRegime?.cautionary ?? 0) - (priorRegime?.cautionary ?? 0))}
            </div>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Recessionary Regime</div>
            <div className="text-2xl font-semibold" style={{ color: chart.recessionary }}>{pct(latestRegime?.recessionary)}</div>
            <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>
              Secondary regime probability • Δ vs prior: {deltaPct((latestRegime?.recessionary ?? 0) - (priorRegime?.recessionary ?? 0))}
            </div>
          </CardContent>
        </Card>
        <Card style={cardStyle}>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Dashboard Risk Status</div>
            <div className="text-2xl font-semibold" style={{ color: toneColor }}>{dashboardRisk.label}</div>
            <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>
              Rolling weakening: {pct(ew?.rolling_weakening_prob ?? latestRegime?.weakening)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card style={cardStyle}>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-medium">Walk-Forward Production Validation</h3>
              <p className="text-xs" style={{ color: ui.textMuted }}>
                Out-of-sample certification layer for the economic model. This validates probability bands; it does not authorize trading.
              </p>
            </div>
            <span
              className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap"
              style={{ backgroundColor: certTone.bg, color: certTone.color, border: `1px solid ${certTone.border}` }}
            >
              {formatStatusLabel(wfRecommendation?.status)}
            </span>
          </div>

          {wfRecommendation ? (
            <>
              <div className="rounded-lg p-3" style={softCardStyle}>
                <div className="text-sm font-medium" style={{ color: certTone.color }}>{wfRecommendation.summary || "Production recommendation available."}</div>
                <div className="mt-1 text-xs leading-5" style={{ color: ui.textMuted }}>
                  Use as: {formatStatusLabel(wfRecommendation.model_use)} • Do not use as: {formatStatusLabel(wfRecommendation.do_not_use_as)}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <div className="rounded-lg p-3" style={softCardStyle}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Gate</div>
                  <div className="text-sm font-semibold">{boolLabel(wfRecommendation.quality_gate?.passed)}</div>
                  <div className="text-xs mt-1" style={{ color: ui.textMuted }}>{wfRecommendation.quality_gate?.enabled ? "Hard fail enabled" : "Diagnostic mode"}</div>
                </div>
                <div className="rounded-lg p-3" style={softCardStyle}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Folds</div>
                  <div className="text-sm font-semibold">{wfMetrics?.successful_folds ?? "—"}/{wfMetrics?.requested_folds ?? "—"}</div>
                  <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Walk-forward blocks</div>
                </div>
                <div className="rounded-lg p-3" style={softCardStyle}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>OOS Rows</div>
                  <div className="text-sm font-semibold">{wfMetrics?.rows ?? "—"}</div>
                  <div className="text-xs mt-1" style={{ color: ui.textMuted }}>{wfMetrics?.date_range?.length ? `${fmtLocalDate(wfMetrics.date_range[0])} → ${fmtLocalDate(wfMetrics.date_range[1])}` : "Walk-forward sample"}</div>
                </div>
                <div className="rounded-lg p-3" style={softCardStyle}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Sentinel Events</div>
                  <div className="text-sm font-semibold">{ratio(wfRecommendation.headline_threshold_rows?.sentinel?.all_events_detection_rate)}</div>
                  <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Early radar detection</div>
                </div>
                <div className="rounded-lg p-3" style={softCardStyle}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Watch FPR</div>
                  <div className="text-sm font-semibold">{ratio(wfRecommendation.headline_threshold_rows?.watch?.false_positive_rate)}</div>
                  <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Formal watch noise</div>
                </div>
                <div className="rounded-lg p-3" style={softCardStyle}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Calibration ECE</div>
                  <div className="text-sm font-semibold">{num(wfCalibration?.expected_calibration_error, 3)}</div>
                  <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Walk-forward calibration</div>
                </div>
              </div>

              {bandRows.length ? (
                <div>
                  <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Operating Bands</div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    {bandRows.map(({ key, band }) => (
                      <div key={key} className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-sm font-semibold">{bandLabel(key)}</div>
                        <div className="text-xs mt-1" style={{ color: ui.textMuted }}>{rangeLabel(band)}</div>
                        <div className="text-xs mt-1 leading-4" style={{ color: ui.textSecondary }}>{band?.use || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="rounded-lg p-3" style={softCardStyle}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>How to read lead time and band crossings</div>
                <div className="text-sm leading-6" style={{ color: ui.textSecondary }}>
                  Lead is historical runway, not a countdown clock. Sentinel is early radar and can be noisy; Watch is the first formal CIO review band; Caution and Severe are higher-conviction review states. Monthly retrains may re-score the path, so use persistence, rolling probability, SHAP drivers, and macro-bucket confirmation before changing posture.
                </div>
              </div>

              <details>
                <summary className="cursor-pointer text-sm font-medium" style={{ color: ui.textPrimary }}>
                  Threshold diagnostics
                </summary>
                <div className="mt-3 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        {["Band", "Threshold", "Precision", "Recall", "FPR", "Event Detect", "Macro Detect", "Lead"].map((header) => (
                          <th key={header} className="px-3 py-2 text-left" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {thresholdRows.map(({ key, label, row }) => (
                        <tr key={key}>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{label}</td>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{pct(row.threshold, 0)}</td>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{ratio(row.weakening_precision)}</td>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{ratio(row.weakening_recall)}</td>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{ratio(row.false_positive_rate)}</td>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{ratio(row.all_events_detection_rate ?? row.event_detection_rate)}</td>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{ratio(row.macro_deterioration_events_detection_rate)}</td>
                          <td className="px-3 py-2" style={{ borderBottom: `1px solid ${ui.cardBorder}` }}>{row.avg_macro_lead_months_detected != null ? `${num(row.avg_macro_lead_months_detected, 1)}m` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          ) : (
            <div className="rounded-lg p-3 text-sm" style={{ ...softCardStyle, color: ui.textSecondary }}>
              Walk-forward production recommendation is not published yet. The rest of the ML tab remains usable.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card style={cardStyle} className="xl:col-span-2">
          <CardContent className="p-4">
            <div className="mb-3">
              <h3 className="font-medium">Forecast Probability Trend</h3>
              <p className="text-xs" style={{ color: ui.textMuted }}>Latest model history window from regime_probs.csv</p>
            </div>
            <Plot
              data={[
                {
                  x: recentRows.map((row) => row.date),
                  y: recentRows.map((row) => row.expansionary * 100),
                  type: "scatter",
                  mode: "lines",
                  name: "Expansionary",
                  line: { width: 2, color: chart.expansionary },
                  hovertemplate: "%{x}<br>Expansionary: %{y:.1f}%<extra></extra>",
                },
                {
                  x: recentRows.map((row) => row.date),
                  y: recentRows.map((row) => row.cautionary * 100),
                  type: "scatter",
                  mode: "lines",
                  name: "Cautionary",
                  line: { width: 2, color: chart.cautionary },
                  hovertemplate: "%{x}<br>Cautionary: %{y:.1f}%<extra></extra>",
                },
                {
                  x: recentRows.map((row) => row.date),
                  y: recentRows.map((row) => row.recessionary * 100),
                  type: "scatter",
                  mode: "lines",
                  name: "Recessionary",
                  line: { width: 2, color: chart.recessionary },
                  hovertemplate: "%{x}<br>Recessionary: %{y:.1f}%<extra></extra>",
                },
              ]}
              layout={{
                autosize: true,
                height: 290,
                margin: { l: 40, r: 20, t: 10, b: 36 },
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                font: { color: chart.axisText, size: 12 },
                legend: { orientation: "h", y: 1.15, x: 0, font: { color: chart.axisText } },
                xaxis: { showgrid: false, tickfont: { color: chart.mutedText }, automargin: true },
                yaxis: {
                  gridcolor: chart.gridLine,
                  zerolinecolor: chart.zeroLine,
                  tickfont: { color: chart.mutedText },
                  automargin: true,
                  ticksuffix: "%",
                },
              }}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: "100%", height: "290px" }}
              useResizeHandler
            />
          </CardContent>
        </Card>

        <Card style={cardStyle}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-medium">Dashboard Risk Layer</h3>
                <p className="text-xs" style={{ color: ui.textMuted }}>Presentation-layer interpretation for human decision support, separate from the raw model label.</p>
              </div>
              <span
                className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap"
                style={{ backgroundColor: toneBg, color: toneColor, border: `1px solid ${toneBorder}` }}
              >
                {dashboardRisk.label}
              </span>
            </div>

            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Current Read</div>
              <div className="text-base font-semibold" style={{ color: toneColor }}>{dashboardRisk.summary}</div>
              <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>{dashboardRisk.detail}</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg p-3" style={softCardStyle}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Weakening</div>
                <div className="text-sm font-medium">{pct(ew?.rolling_weakening_prob ?? latestRegime?.weakening)}</div>
                <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Rolling probability</div>
              </div>
              <div className="rounded-lg p-3" style={softCardStyle}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>3-Month Trend</div>
                <div className="text-sm font-medium">{deltaPct(ew?.delta_3m ?? ew?.latest_trigger?.delta_3m)}</div>
                <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Direction of change</div>
              </div>
            </div>

            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Latest Escalation</div>
              {ew?.latest_trigger ? (
                <div className="text-sm" style={{ color: ui.textSecondary }}>
                  <div>{fmtLocalDate(ew.latest_trigger.date)}</div>
                  <div>Weakening: {pct(ew.latest_trigger.weakening_prob)}</div>
                  <div>Δ 3m: {deltaPct(ew.latest_trigger.delta_3m)}</div>
                </div>
              ) : (
                <div className="text-sm" style={{ color: ui.textMuted }}>No escalation on file.</div>
              )}
            </div>

            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Latest Improvement</div>
              {ew?.latest_recovery ? (
                <div className="text-sm" style={{ color: ui.textSecondary }}>
                  <div>{fmtLocalDate(ew.latest_recovery.date)}</div>
                  <div>Weakening: {pct(ew.latest_recovery.weakening_prob)}</div>
                  <div>Δ 3m: {deltaPct(ew.latest_recovery.delta_3m)}</div>
                </div>
              ) : (
                <div className="text-sm" style={{ color: ui.textMuted }}>No improvement event has been logged yet.</div>
              )}
            </div>

            <div className="text-xs" style={{ color: ui.textMuted }}>
              Generated: {ew?.generated_at ? fmtLocalDateTime(ew.generated_at) : "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card style={cardStyle} className="xl:col-span-2">
          <CardContent className="p-4">
            <div className="mb-3">
              <h3 className="font-medium">Recent Regime Probability Table</h3>
              <p className="text-xs" style={{ color: ui.textMuted }}>Newest 24 rows from the published regime_probs artifact.</p>
            </div>
            {recentTableRows.length ? (
              <div className="overflow-auto max-h-[420px]">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      {headers.map((header, idx) => (
                        <th
                          key={idx}
                          className="px-3 py-2 text-left sticky top-0"
                          style={{
                            backgroundColor: ui.cardBg,
                            color: ui.textPrimary,
                            borderBottom: `1px solid ${ui.cardBorder}`,
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentTableRows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="px-3 py-2 whitespace-nowrap"
                            style={{
                              color: ui.textSecondary,
                              borderBottom: `1px solid ${ui.cardBorder}`,
                              backgroundColor: rowIdx % 2 === 0 ? "transparent" : chart.tableAlt,
                            }}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: ui.textMuted }}>No regime probability file available.</p>
            )}
          </CardContent>
        </Card>

        <Card style={cardStyle}>
          <CardContent className="p-4 space-y-3">
            <div>
              <h3 className="font-medium">Artifact Health</h3>
              <p className="text-xs" style={{ color: ui.textMuted }}>Keep the plumbing visible so the frontend is not guessing in the dark.</p>
            </div>

            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Model File</div>
              <div className="text-sm" style={{ color: ui.textSecondary }}>
                <div>Updated: {latest.files.model?.last_modified ? fmtLocalDateTime(latest.files.model.last_modified) : "—"}</div>
                <div>Size: {latest.files.model?.size ? `${Math.round(latest.files.model.size / 1024)} KB` : "—"}</div>
              </div>
            </div>

            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>SHAP Image</div>
              <div className="text-sm" style={{ color: ui.textSecondary }}>
                <div>Updated: {latest.files.shap_png?.last_modified ? fmtLocalDateTime(latest.files.shap_png.last_modified) : "—"}</div>
                <div>Available: {latest.files.shap_png ? "Yes" : "No"}</div>
              </div>
            </div>

            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Early Warning Records</div>
              <div className="text-sm" style={{ color: ui.textSecondary }}>
                <div>Escalations: {ew?.triggers?.length ?? 0}</div>
                <div>Improvements: {ew?.recoveries?.length ?? 0}</div>
                <div>Radar state: {formatStatusLabel(ew?.radar_state || dashboardRisk.status)}</div>
                <div>Caution band: {pct(ew?.thresholds?.caution ?? ew?.threshold)}</div>
              </div>
            </div>

            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Walk-Forward Artifacts</div>
              <div className="text-sm" style={{ color: ui.textSecondary }}>
                <div>Recommendation: {wfRecommendation ? "Available" : "Missing"}</div>
                <div>Metrics: {wfMetrics ? "Available" : "Missing"}</div>
                <div>Calibration: {wfCalibration ? "Available" : "Missing"}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card style={cardStyle}>
        <CardContent className="p-4">
          <div className="mb-3">
            <h3 className="font-medium">Feature Importance (SHAP)</h3>
            <p className="text-xs" style={{ color: ui.textMuted }}>
              Updated {latest?.files?.shap_png?.last_modified ? fmtLocalDateTime(latest.files.shap_png.last_modified) : "—"}
            </p>
          </div>
          <img
            src={dl.shap_png}
            alt="SHAP Feature Importance"
            className="rounded border max-w-full h-auto"
            style={{ borderColor: ui.cardBorder }}
          />
        </CardContent>
      </Card>

      <details>
        <summary className="cursor-pointer text-sm font-medium" style={{ color: ui.textPrimary }}>
          Technical downloads and raw artifacts
        </summary>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: "macro_features.csv", href: dl.macro_features, note: latest.files.macro_features?.last_modified },
            { label: "macro_labels.csv", href: dl.macro_labels, note: latest.files.macro_labels?.last_modified },
            { label: "macro_model.pkl", href: dl.model, note: latest.files.model?.last_modified },
            { label: "early_warning.csv", href: dl.early_warning_csv, note: latest.files.early_warning_csv?.last_modified },
            { label: "walk_forward_production_recommendation.json", href: dl.walk_forward_production_recommendation, note: latest.files.walk_forward_production_recommendation?.last_modified },
            { label: "walk_forward_backtest_metrics.json", href: dl.walk_forward_backtest_metrics, note: latest.files.walk_forward_backtest_metrics?.last_modified },
            { label: "walk_forward_threshold_sweep.json", href: dl.walk_forward_threshold_sweep, note: latest.files.walk_forward_threshold_sweep?.last_modified },
            { label: "walk_forward_calibration_report.json", href: dl.walk_forward_calibration_report, note: latest.files.walk_forward_calibration_report?.last_modified },
            { label: "walk_forward_false_signal_ledger.csv", href: dl.walk_forward_false_signal_ledger, note: latest.files.walk_forward_false_signal_ledger?.last_modified },
            { label: "walk_forward_fold_metrics.csv", href: dl.walk_forward_fold_metrics, note: latest.files.walk_forward_fold_metrics?.last_modified },
          ].map((item) => (
            <Card key={item.label} style={cardStyle}>
              <CardContent className="p-4">
                <div className="font-medium mb-2">{item.label}</div>
                <div className="text-xs mb-3" style={{ color: ui.textMuted }}>
                  {item.note ? `Updated ${fmtLocalDateTime(item.note)}` : "Timestamp unavailable"}
                </div>
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-sm"
                  style={{ color: chart.link }}
                >
                  Open artifact
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </details>
    </div>
  );
};

export default MLResultsTab;
